import {Component, OnInit, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {CodaltComponent} from '../../codalt.component';
import {Checkpoint, level as LevelEnum} from '../../classes/checkpoint';
import {CheckpointService} from '../../services/checkpoint.service';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatFormField, MatInput} from '@angular/material/input';
import {NgOptionComponent, NgSelectComponent} from '@ng-select/ng-select';
import {Platform} from '../../classes/platform';
import {Utils} from '../../utils.class';
import {PlatformService} from '../../services/platform.service';
import {Routenames} from '../../classes/route-name.enum';
import {FieldErrorComponent} from '../../field-error/field-error.component';
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from '@angular/material/datepicker';
import {BreadcrumbComponent} from '../../services/breadcrumb/breadcrumb.component';
import {BytesPipePipe} from '../../pipes/bytes.pipe';
import {HasFeaturePipe} from '../../pipes/has-feature.pipe';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {MsReadablePipe} from '../../pipes/milliseconds-to-readable-time.pipe';
import {MatTooltip} from '@angular/material/tooltip';

@Component({
    selector: 'app-checkpoint',
    templateUrl: './checkpoint-store.component.html',
    styleUrls: ['./checkpoint-store.component.scss'],
    imports: [
        FormsModule,
        MatFormField,
        MatInput,
        NgSelectComponent,
        ReactiveFormsModule,
        NgOptionComponent,
        FieldErrorComponent,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        BreadcrumbComponent,
        BytesPipePipe,
        HasFeaturePipe,
        MatIcon,
        MatIconButton,
        MsReadablePipe,
        MatTooltip
    ]
})
export class CheckpointStoreComponent extends CodaltComponent implements OnInit {
    readonly form = signal<FormGroup | null>(null);
    readonly platforms = signal<Platform[]>([]);
    checkpointLevels = LevelEnum;
    minDate: Date;
    platformId: number;
    saving = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private checkpointService: CheckpointService,
        private confirmDialog: ConfirmDialogService,
        private platformService: PlatformService,
        private router: Router
    ) {
        super();
    }

    ngOnInit() {
        this.subscriptions.add(this.platformService.getPlatforms().subscribe(platforms => {
            this.platforms.set(platforms);
            this.crumbs = [
                {name: 'Seire', route: Routenames.seire},
                {name: `Platform : ${this.platforms().find(platform => platform.id === this.platformId)?.name}`, route: `${Routenames.seire}/${this.platformId}`},
                {name: `Checkpoint : ${this.form().get('name').value ?? 'new'}`, route: `${Routenames.seire}/${this.platformId}/store/${this.form().get('id').value ?? 'new'}`}
            ];
        }));

        this.subscriptions.add(this.activatedRoute.params.subscribe((params: {platformId, checkId}) => {
            this.platformId = +params.platformId;
            if (params.checkId === 'new') {
                this.createForm(new Checkpoint());
            } else {
                this.subscriptions.add(
                    this.checkpointService.getCheckpoint(+params.checkId).subscribe({
                        next: res => this.createForm(res),
                        error: err => console.error(err)
                    })
                );
            }
        }));
    }

    createForm(checkpoint: Checkpoint) {
        const startDate = new Date();
        Utils.setTime(startDate, 0, 0, 0);
        const endDate = new Date(startDate);
        Utils.setTime(endDate, 23, 59, 0);

        if (!checkpoint.id || !Utils.isPast(checkpoint.start_date)) {
            this.minDate = new Date(startDate);
            this.minDate.setDate(this.minDate.getDate() - 100);
        } else {
            this.minDate = new Date(checkpoint.start_date);
        }

        this.form.set(new FormGroup({
            id: new FormControl(checkpoint.id),
            platform_id: new FormControl(checkpoint.platform_id ?? this.platformId, Validators.required),
            responsible_user_id: new FormControl(checkpoint.responsible_user_id),
            fallback_responsible_user_id: new FormControl(checkpoint.fallback_responsible_user_id),
            url: new FormControl(checkpoint.url),
            start_date: new FormControl(checkpoint.start_date ? new Date(checkpoint.start_date) : startDate, Validators.required),
            end_date: new FormControl(checkpoint.end_date ? new Date(checkpoint.end_date) : checkpoint.end_date),
            name: new FormControl(checkpoint.name, Validators.required),

            validate_ssl: new FormControl(checkpoint.validate_ssl ?? true),
            valid_response_code: new FormControl(checkpoint.valid_response_code ?? 200, Validators.required),
            warning_response_code: new FormControl(checkpoint.warning_response_code),
            level: new FormControl(checkpoint.level ?? LevelEnum.PRODUCTION, Validators.required),
            retries: new FormControl(checkpoint.retries ?? 3, Validators.required),
            retry_delay: new FormControl(checkpoint.retry_delay ?? 30000, Validators.required),
            min_content_size_error: new FormControl(checkpoint.min_content_size_error ?? 10, Validators.required),
            max_content_size_error: new FormControl(checkpoint.max_content_size_error ?? 500000, Validators.required),
            min_content_size_warning: new FormControl(checkpoint.min_content_size_warning),
            max_content_size_warning: new FormControl(checkpoint.max_content_size_warning),
            timeout: new FormControl(checkpoint.timeout ?? 10000, Validators.required),
            interval: new FormControl(checkpoint.interval ?? 60000, Validators.required),
            valid_checksum: new FormControl(checkpoint.valid_checksum),
            min_response_time_error: new FormControl(checkpoint.min_response_time_error ?? 10, Validators.required),
            max_response_time_error: new FormControl(checkpoint.max_response_time_error ?? 10000, Validators.required),
            min_response_time_warning: new FormControl(checkpoint.min_response_time_warning),
            max_response_time_warning: new FormControl(checkpoint.max_response_time_warning),
            save_contents_on_success: new FormControl(checkpoint.save_contents_on_success ?? false),
            external_ip_allowed: new FormControl(checkpoint.external_ip_allowed),
            updated_at: new FormControl(checkpoint.updated_at)
        }));
    }

    openDelete(event) {
        event.preventDefault();
        event.stopPropagation();

        const checkpoint = new Checkpoint();
        Object.assign(checkpoint, this.form().value);

        this.confirmDialog
            .confirm(
                'Verwijderen',
                `Weet u zeker dat u ${checkpoint.name} wilt verwijderen?`,
                'Verwijderen',
                'Behouden'
            )
            .then(() => {
                this.checkpointService.deleteCheckpoint(checkpoint.id).then(() => {
                    this.router.navigate([Routenames.seire, this.platformId]);
                });
            })
            .catch(() => {
                console.log('checkpoint not deleted');
            });
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form().valid) {
            const checkpoint = new Checkpoint();
            Object.assign(checkpoint, this.form().value);

            if (checkpoint.external_ip_allowed === '') {
                checkpoint.external_ip_allowed = null;
            }

            this.subscriptions.add(
                this.checkpointService.saveCheckpoint(checkpoint).subscribe(() => {
                    this.router.navigate([Routenames.seire, checkpoint.platform_id]);
                })
            );
        }
    }
}
